import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Grid, TextField, Switch, Button, Alert, Snackbar, Divider,
} from '@mui/material';
import { Save, RotateCcw, Percent, Mail, Smartphone, Bell, CalendarX, UserPlus, ShieldCheck } from 'lucide-react';
import api from '../config/api';
import { FormSkeleton } from '../components/TableShared';

const ff = "'Poppins', sans-serif";

const SectionCard = ({ title, subtitle, icon, children }) => (
  <Paper elevation={0} sx={{
    p: 3, borderRadius: '14px', border: '1px solid #EDF2F7', height: '100%',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '10px', background: '#EBF8FF',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Box>
        <Box sx={{ fontFamily: ff, fontSize: 14, fontWeight: 600, color: '#1A202C' }}>{title}</Box>
        {subtitle && <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>{subtitle}</Box>}
      </Box>
    </Box>
    <Divider sx={{ my: 2, borderColor: '#EDF2F7' }} />
    {children}
  </Paper>
);

const ToggleRow = ({ label, description, checked, onChange, icon }) => (
  <Box sx={{
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    py: 1.5, px: 1, borderRadius: '10px',
    transition: 'background 0.15s',
    '&:hover': { background: '#F7FAFC' },
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon && <Box sx={{ color: '#718096', display: 'flex' }}>{icon}</Box>}
      <Box>
        <Box sx={{ fontFamily: ff, fontSize: 13, fontWeight: 500, color: '#2D3748' }}>{label}</Box>
        {description && <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0' }}>{description}</Box>}
      </Box>
    </Box>
    <Switch
      checked={checked}
      onChange={onChange}
      sx={{
        '& .MuiSwitch-switchBase.Mui-checked': { color: '#32A6D8' },
        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#32A6D8' },
      }}
    />
  </Box>
);

export default function Settings() {
  const [settings, setSettings] = useState({
    platformFeePercentage: 10,
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    newBookingNotification: true,
    bookingCancelledNotification: true,
    newUserNotification: true,
    sitterApprovalNotification: true,
  });
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/settings');
      const data = res.data.data || res.data;
      if (data) {
        setSettings(prev => {
          const merged = { ...prev, ...data };
          setOriginal(merged);
          return merged;
        });
      }
    } catch (e) {
      // Use defaults silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const toggle = (key) => setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  const hasChanges = JSON.stringify(settings) !== JSON.stringify(original);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/admin/settings', settings);
      setOriginal({ ...settings });
      setSnackbar({ open: true, message: 'Settings saved successfully', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save settings', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (original) setSettings({ ...original });
  };

  if (loading) return <FormSkeleton />;

  return (
    <Box>
      <Grid container spacing={2.5}>
        {/* Platform Fee */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Platform Fee"
            subtitle="Transaction fee percentage"
            icon={<Percent size={18} color="#32A6D8" />}
          >
            <Box sx={{ fontFamily: ff, fontSize: 12, color: '#718096', mb: 2 }}>
              Set the percentage fee charged on each completed booking.
            </Box>
            <TextField
              type="number"
              value={settings.platformFeePercentage}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v) && v >= 0 && v <= 100) {
                  setSettings(prev => ({ ...prev, platformFeePercentage: v }));
                }
              }}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: ff, fontSize: 14, borderRadius: '10px',
                  '& fieldset': { borderColor: '#EDF2F7' },
                  '&:hover fieldset': { borderColor: '#CBD5E0' },
                  '&.Mui-focused fieldset': { borderColor: '#32A6D8' },
                },
              }}
              InputProps={{
                endAdornment: <Box sx={{ fontFamily: ff, fontSize: 13, color: '#A0AEC0', mr: 0.5 }}>%</Box>,
              }}
            />
            <Box sx={{ fontFamily: ff, fontSize: 11, color: '#A0AEC0', mt: 1 }}>
              Current fee: {settings.platformFeePercentage}% per transaction
            </Box>
          </SectionCard>
        </Grid>

        {/* Notification Channels */}
        <Grid item xs={12} md={6}>
          <SectionCard
            title="Notification Channels"
            subtitle="Active delivery methods"
            icon={<Bell size={18} color="#32A6D8" />}
          >
            <ToggleRow
              label="Email Notifications"
              description="Send notifications via email"
              icon={<Mail size={16} />}
              checked={settings.emailNotifications}
              onChange={() => toggle('emailNotifications')}
            />
            <ToggleRow
              label="Push Notifications"
              description="Send push notifications to devices"
              icon={<Smartphone size={16} />}
              checked={settings.pushNotifications}
              onChange={() => toggle('pushNotifications')}
            />
            <ToggleRow
              label="SMS Notifications"
              description="Send SMS text messages"
              icon={<Smartphone size={16} />}
              checked={settings.smsNotifications}
              onChange={() => toggle('smsNotifications')}
            />
          </SectionCard>
        </Grid>

        {/* Notification Events */}
        <Grid item xs={12}>
          <SectionCard
            title="Notification Events"
            subtitle="Which events trigger admin alerts"
            icon={<Bell size={18} color="#32A6D8" />}
          >
            <Grid container spacing={0}>
              <Grid item xs={12} sm={6}>
                <ToggleRow
                  label="New Booking Created"
                  description="Alert when a new booking is made"
                  icon={<CalendarX size={16} />}
                  checked={settings.newBookingNotification}
                  onChange={() => toggle('newBookingNotification')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ToggleRow
                  label="Booking Cancelled"
                  description="Alert when a booking is cancelled"
                  icon={<CalendarX size={16} />}
                  checked={settings.bookingCancelledNotification}
                  onChange={() => toggle('bookingCancelledNotification')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ToggleRow
                  label="New User Registration"
                  description="Alert when a new user signs up"
                  icon={<UserPlus size={16} />}
                  checked={settings.newUserNotification}
                  onChange={() => toggle('newUserNotification')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ToggleRow
                  label="Sitter Approval Request"
                  description="Alert when a sitter applies for approval"
                  icon={<ShieldCheck size={16} />}
                  checked={settings.sitterApprovalNotification}
                  onChange={() => toggle('sitterApprovalNotification')}
                />
              </Grid>
            </Grid>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Actions */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={!hasChanges || saving}
          startIcon={<RotateCcw size={16} />}
          sx={{
            fontFamily: ff, fontSize: 13, textTransform: 'none', borderRadius: '10px',
            borderColor: '#EDF2F7', color: '#718096',
            '&:hover': { borderColor: '#CBD5E0', background: '#F7FAFC' },
          }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          startIcon={<Save size={16} />}
          sx={{
            fontFamily: ff, fontSize: 13, textTransform: 'none', borderRadius: '10px',
            background: '#32A6D8', px: 3,
            '&:hover': { background: '#2892C0' },
            '&.Mui-disabled': { background: '#EDF2F7', color: '#A0AEC0' },
          }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ fontFamily: ff }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
